import {
  useState,
  useEffect,
  useReducer,
  useContext,
  useCallback,
} from 'react';
import toastError from '../../toasts/toastError';
import { toast } from 'react-toastify';

import {
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Grid,
  useTheme,
} from '@mui/material';

import { CheckCircle, DeleteOutline, Edit, Search } from '@mui/icons-material';

import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import Title from '../../components/Title';
import TableRowSkeleton from '../../components/TableRowSkeleton';
import QuickMessageDialog from '../../components/QuickMessageDialog';
import ConfirmationModal from '../../components/ConfirmationModal';

import api from '../../services/api';
import { i18n } from '../../translate/i18n';
import { isArray } from 'lodash';
import { AuthContext } from '../../context/Auth/AuthContext';
import { getStyled } from './styles';

const reducer = (state, action) => {
  if (action.type === 'LOAD_QUICKMESSAGES') {
    const quickmessages = action.payload;
    const newQuickmessages = [];

    if (isArray(quickmessages)) {
      quickmessages.forEach((quickemessage) => {
        const quickemessageIndex = state.findIndex(
          (u) => u.id === quickemessage.id,
        );
        if (quickemessageIndex !== -1) {
          state[quickemessageIndex] = quickemessage;
        } else {
          newQuickmessages.push(quickemessage);
        }
      });
    }

    return [...state, ...newQuickmessages];
  }

  if (action.type === 'UPDATE_QUICKMESSAGES') {
    const quickemessage = action.payload;
    const quickemessageIndex = state.findIndex(
      (u) => u.id === quickemessage.id,
    );

    if (quickemessageIndex !== -1) {
      state[quickemessageIndex] = quickemessage;
      return [...state];
    } else {
      return [quickemessage, ...state];
    }
  }

  if (action.type === 'DELETE_QUICKMESSAGE') {
    const quickemessageId = action.payload;

    const quickemessageIndex = state.findIndex((u) => u.id === quickemessageId);
    if (quickemessageIndex !== -1) {
      state.splice(quickemessageIndex, 1);
    }
    return [...state];
  }

  if (action.type === 'RESET') {
    return [];
  }
};

const QuickMessages = () => {
  const theme = useTheme();
  const classes = getStyled(theme);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedQuickemessage, setSelectedQuickemessage] = useState(null);
  const [deletingQuickemessage, setDeletingQuickemessage] = useState(null);
  const [quickemessageModalOpen, setQuickMessageDialogOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState('');
  const [quickemessages, dispatch] = useReducer(reducer, []);
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    dispatch({ type: 'RESET' });
  }, [searchParam]);

  const fetchQuickemessages = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/quick-messages', {
        params: { searchParam, pageNumber },
      });

      dispatch({ type: 'LOAD_QUICKMESSAGES', payload: data.records });
      setHasMore(data.hasMore);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, searchParam]);

  useEffect(() => {
    fetchQuickemessages();
  }, [searchParam, pageNumber, fetchQuickemessages]);

  useEffect(() => {
    const companyId = user.companyId;

    const onQuickMessageEvent = (data) => {
      if (data.action === 'update' || data.action === 'create') {
        dispatch({ type: 'UPDATE_QUICKMESSAGES', payload: data.record });
      }
      if (data.action === 'delete') {
        dispatch({ type: 'DELETE_QUICKMESSAGE', payload: +data.id });
      }
    };
    socket.on(`company-${companyId}-quickemessage`, onQuickMessageEvent);

    return () => {
      socket.off(`company-${companyId}-quickemessage`, onQuickMessageEvent);
    };
  }, [socket, user]);

  const handleOpenQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(true);
  };

  const handleCloseQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(false);
    fetchQuickemessages();
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditQuickemessage = (quickemessage) => {
    setSelectedQuickemessage(quickemessage);
    setQuickMessageDialogOpen(true);
  };

  const handleDeleteQuickemessage = async (quickemessageId) => {
    try {
      await api.delete(`/quick-messages/${quickemessageId}`);
      toastSuccess(i18n.t('quickemessages.toasts.deleted'));
    } catch (err) {
      toastError(err);
    }
    setDeletingQuickemessage(null);
    setSearchParam('');
    setPageNumber(1);
    fetchQuickemessages();
    dispatch({ type: 'RESET' });
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingQuickemessage &&
          `${i18n.t('quickMessages.confirmationModal.deleteTitle')} ${
            deletingQuickemessage.shortcode
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteQuickemessage(deletingQuickemessage.id)}
      >
        {i18n.t('quickMessages.confirmationModal.deleteMessage')}
      </ConfirmationModal>
      <QuickMessageDialog
        resetPagination={() => {
          setPageNumber(1);
          fetchQuickemessages();
        }}
        open={quickemessageModalOpen}
        onClose={handleCloseQuickMessageDialog}
        aria-labelledby='form-dialog-title'
        quickemessageId={selectedQuickemessage && selectedQuickemessage.id}
      />
      <MainHeader>
        <Grid style={{ width: '99.6%' }} container>
          <Grid size={{ xs: 12, sm: 8 }}>
            <Title>{i18n.t('quickMessages.title')}</Title>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Grid spacing={2} container>
              <Grid size={{ xs: 6, sm: 6 }}>
                <TextField
                  sx={classes.searchField}
                  variant='standard'
                  fullWidth
                  placeholder={i18n.t('quickMessages.searchPlaceholder')}
                  type='search'
                  value={searchParam}
                  onChange={handleSearch}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position='start'>
                          <Search style={{ color: 'gray' }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 6 }}>
                <Button
                  fullWidth
                  variant='outlined'
                  onClick={handleOpenQuickMessageDialog}
                  sx={theme.custom.editButton}
                >
                  {i18n.t('quickMessages.buttons.add')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MainHeader>
      <Paper sx={classes.mainPaper} variant='outlined' onScroll={handleScroll}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell align='center'>
                {i18n.t('quickMessages.table.shortcode')}
              </TableCell>

              <TableCell align='center'>
                {i18n.t('quickMessages.table.mediaName')}
              </TableCell>
              <TableCell align='center'>
                {i18n.t('quickMessages.table.status')}
              </TableCell>
              <TableCell align='center'>
                {i18n.t('quickMessages.table.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {quickemessages.map((quickemessage) => (
                <TableRow key={quickemessage.id}>
                  <TableCell align='center'>
                    {quickemessage.shortcode}
                  </TableCell>

                  <TableCell align='center'>
                    {quickemessage.mediaName ??
                      i18n.t('quickMessages.noAttachment')}
                  </TableCell>
                  <TableCell align='center'>
                    {quickemessage.geral === true ? (
                      <CheckCircle style={{ color: 'green' }} />
                    ) : (
                      ''
                    )}
                  </TableCell>
                  <TableCell align='center'>
                    <IconButton
                      size='small'
                      onClick={() => handleEditQuickemessage(quickemessage)}
                    >
                      <Edit />
                    </IconButton>

                    <IconButton
                      size='small'
                      onClick={() => {
                        setConfirmModalOpen(true);
                        setDeletingQuickemessage(quickemessage);
                      }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={5} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default QuickMessages;
